import React from 'react';
import Select from 'components/v2/Select/Select';
import { Controller, useFormContext } from 'react-hook-form';
import { FieldArgs, SelectField } from '../types';
import InputLabel from '../../InputLabel/InputLabel';
import FormControl from 'components/v2/FormControl/FormControl';
import { useFormError } from '../utils';
import FormError from '../../FormError/FormError';
import get from 'lodash/get';
import { useTranslation } from 'utils/translation';
import TextInput from '../../Input/TextInput';
import Spacer from '../../Spacer/Spacer';

export type SelectAdapterProps = FieldArgs<SelectField>;

const SelectAdapter = (props: SelectAdapterProps) => {
  const { t } = useTranslation();
  const { label, name, rules, options, allowCustomChoice = false } = props;
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = useFormError(get(errors, name));
  const [customChoice, setCustomChoice] = React.useState(false);

  const handleRegularChoiceClick = () => {
    setCustomChoice(false);
  };

  const handleCustomChoiceClick = () => {
    setCustomChoice(true);
  };

  return (
    <FormControl error={!!error}>
      <InputLabel>{label}</InputLabel>
      <Controller
        render={({ field: { ref, value, ...field } }) => {
          const hasCustomValue = (() => {
            if (!allowCustomChoice) {
              return false;
            }

            return !options.find(
              (item) => item.value.toString() === String(value),
            );
          })();

          return (
            <>
              <Select
                {...field}
                value={value || null}
                onChange={(event, value) => field.onChange(value)}
              >
                <Select.Option value={null} disabled={rules?.required}>
                  ---
                </Select.Option>
                {options.map(({ value, label }) => (
                  <Select.Option
                    key={value}
                    value={value}
                    onClickCapture={handleRegularChoiceClick}
                  >
                    {label}
                  </Select.Option>
                ))}
                {allowCustomChoice && (
                  <Select.Option
                    value={hasCustomValue ? value : ''}
                    onClickCapture={handleCustomChoiceClick}
                  >
                    {t('intelligence_layer_select_field_custom_choice')}
                  </Select.Option>
                )}
              </Select>
              {(customChoice || hasCustomValue) && (
                <>
                  <Spacer />
                  <TextInput
                    placeholder={label}
                    name={name}
                    value={value}
                    onChange={field.onChange}
                  />
                </>
              )}
            </>
          );
        }}
        control={control}
        name={name}
        rules={rules}
      />
      <FormError error={error} />
    </FormControl>
  );
};

export default SelectAdapter;
